'use client';

import type { EventProperties, UserTraits } from '@segment/analytics-next';
import type { ExtraContext } from '@segment/analytics-node/dist/types/app/types';

import { nextBackendClient } from '@/api/v2/api';
import { COUNTRY_COOKIE, STATSIG_COOKIE } from '@/app/consts/cookies';
import { getCookie } from '@/utils/cookie';
import isBrowser from '@/utils/isBrowser';

import { OneTrustAnalyticsRouter } from './oneTrustAnalyticsRouter';
import { OneTrustGateHandler } from './oneTrustGateHandler';
import type {
  BaseDataLayer,
  GA4PayloadType,
  PartialGA4ItemType,
  PayloadItems,
} from './types';
import { canUseClientSideAnalytics, getRedditCid } from './utils';

interface DataLayer extends BaseDataLayer {
  ecommerce_data?: PartialGA4ItemType | GA4PayloadType;
  eventPath?: string | undefined;
  eventAttributes?: PayloadItems;
}

export class OuraAnalytics {
  private serverBasePath: string;
  private statsigStableId: string | null;
  // Gate handler and router: permanent for GDPR-compliant analytics
  // During productization: gate handler will be simplified (remove gate tracking)
  private gateHandler = new OneTrustGateHandler();
  private oneTrustRouter = new OneTrustAnalyticsRouter(this.gateHandler);

  constructor({
    serverBasePath = '/api/analytics',
    statsigStableId = getCookie(STATSIG_COOKIE),
  }: {
    serverBasePath?: string;
    statsigStableId?: string | null;
  } = {}) {
    this.serverBasePath = serverBasePath;
    this.statsigStableId = statsigStableId;
  }

  /**
   * Update the OneTrust gate enabled status
   *
   * REMOVE DURING PRODUCTIZATION (~2 weeks post-rollout)
   *
   * Current purpose (during rollout):
   * Called by ConsentManagerProvider when Statsig determines which CM is active.
   * Just updates internal gate state - no queueing.
   *
   * After productization:
   * - OneTrust will be the only consent manager
   * - This method is no longer needed
   * - Router will work without gate checks
   */
  setOneTrustGateEnabled(enabled: boolean) {
    this.gateHandler.setGateValue(enabled);
  }

  async track(
    event: string,
    properties?: EventProperties,
    options?: ExtraContext,
  ) {
    // Check OneTrust routing decision
    const decision = this.oneTrustRouter.routeTrackEvent(event);

    if (decision.action === 'drop') {
      this.debugAnalyticsEvent(`Dropping event: ${event} - ${decision.reason}`);
      return;
    }

    if (decision.action === 'route-server-side') {
      await this.sendTrackEvent({
        event,
        properties,
        options,
        forceServer: true,
      });
      return;
    }

    // Send the event using common logic
    await this.sendTrackEvent({ event, properties, options });
  }

  async page(
    name: string,
    properties?: EventProperties,
    context?: ExtraContext,
  ) {
    // Check OneTrust routing decision
    const decision = this.oneTrustRouter.routePageEvent();

    if (decision.action === 'drop') {
      this.debugAnalyticsEvent(
        `Dropping page event: ${name} - ${decision.reason}`,
      );
      return;
    }

    const forceServer = decision.action === 'route-server-side';
    const type = forceServer
      ? 'server'
      : this.getAnalyticsFn(
          window.analytics.page.bind(window.analytics),
          this.serverPage,
        )[0];
    this.debugAnalyticsEvent(
      `Sending ${type} analytics page`,
      name,
      properties,
      context,
    );
    const enhancedProperties = this.enhanceProperties({ ...properties, name });
    const enhancedContext = this.enhanceContext(context);
    await this.callAnalyticsFn('page', async () => {
      if (type === 'client') {
        return window.analytics.page(name, enhancedProperties, enhancedContext);
      } else {
        return this.serverPage(name, enhancedProperties, enhancedContext);
      }
    });
  }

  async identify(traits: UserTraits | string, context?: ExtraContext) {
    // Check OneTrust routing decision
    const decision = this.oneTrustRouter.routeIdentifyEvent();

    if (decision.action === 'drop') {
      this.debugAnalyticsEvent(`Dropping identify event - ${decision.reason}`);
      return;
    }

    const forceServer = decision.action === 'route-server-side';
    const type = forceServer
      ? 'server'
      : this.getAnalyticsFn(
          window.analytics.identify.bind(window.analytics),
          this.serverIdentify,
        )[0];
    this.debugAnalyticsEvent(
      `Sending ${type} analytics identify`,
      traits,
      context,
    );
    const enhancedContext = this.enhanceContext(context);
    await this.callAnalyticsFn('identify', async () => {
      if (type === 'client') {
        return window.analytics.identify(traits, enhancedContext);
      } else {
        return this.serverIdentify(traits, enhancedContext);
      }
    });
  }

  gtmEvent(event: DataLayer) {
    window.dataLayer?.push(event);
  }

  private getAnalyticsFn = <T, S>(clientFn: T, serverFn: S) => {
    // TODO: Remove after OneTrust CM rollout - pass false instead
    const canClientSideTrack = canUseClientSideAnalytics(
      this.gateHandler.getGateValue(),
    );

    const type = canClientSideTrack ? 'client' : 'server';
    const fn = canClientSideTrack ? clientFn : serverFn;
    return [type, fn] as const;
  };

  private enhanceProperties = (properties: EventProperties | undefined) => {
    // Inject userCountry from cookie
    const userCountry = getCookie(COUNTRY_COOKIE);
    return this.withFacebookPixelCookies(
      this.withRedditProperties(
        this.withStatisgIds(
          this.withPageInfo({
            ...properties,
            ...(userCountry && { userCountry }),
          }),
        ),
      ),
    );
  };

  private enhanceContext = (
    context: ExtraContext | undefined,
  ): ExtraContext => {
    const locale = getCookie('NEXT_LOCALE');
    return {
      page: this.withPageInfo(),
      locale: locale ?? 'en',
      ...context,
    };
  };

  private callAnalyticsFn = async (
    name: string,
    fn: () => Promise<unknown>,
  ) => {
    /**
     * Need to wrap fn because it can be non-Promise-returning function on initial load
     * (Segment queues up events before it's initialized and in that case it does not
     * return a promise).
     */
    return Promise.resolve(fn()).catch((e) => {
      // TODO - do we want to log these errors to datadog?
      this.debugAnalyticsEvent(`Error sending ${name} event`, e);
    });
  };

  private serverTrack = (
    event: string,
    properties?: EventProperties,
    options?: { context?: ExtraContext },
  ) => {
    return nextBackendClient.url(this.serverBasePath + '/track').post({
      event,
      properties,
      context: options?.context,
    });
  };

  private serverIdentify = (
    traits: UserTraits | string,
    context?: ExtraContext,
  ) => {
    return nextBackendClient.url(this.serverBasePath + '/identify').post({
      traits,
      context,
    });
  };

  private serverPage = (
    name: string,
    properties: EventProperties,
    context?: ExtraContext,
  ) => {
    return nextBackendClient.url(this.serverBasePath + '/page').post({
      name,
      properties,
      context,
    });
  };

  /**
   * Send a track event - handles both client and server-side routing
   * Extracted to reduce code duplication
   */
  private async sendTrackEvent({
    event,
    properties,
    options,
    forceServer = false,
  }: {
    event: string;
    properties?: EventProperties | undefined;
    options?: ExtraContext | undefined;
    forceServer?: boolean;
  }) {
    const type = forceServer
      ? 'server'
      : this.getAnalyticsFn(
          window.analytics.track.bind(window.analytics),
          this.serverTrack,
        )[0];

    this.debugAnalyticsEvent(
      `Sending ${type} analytics track`,
      event,
      properties,
      options,
    );

    const enhancedProperties = this.enhanceProperties(properties);
    const enhancedContext = this.enhanceContext(options);

    await this.callAnalyticsFn('track', async () => {
      if (type === 'client') {
        return window.analytics.track(event, enhancedProperties, {
          ...options,
          context: enhancedContext,
        });
      } else {
        return this.serverTrack(event, enhancedProperties, {
          ...options,
          context: enhancedContext,
        });
      }
    });
  }

  /*
   * This is needed to associate segment events to a statsig user
   */
  private withStatisgIds = (properties?: EventProperties) => {
    if (!this.statsigStableId) return properties;
    return {
      ...properties,
      statsigCustomIDs: ['stableID', this.statsigStableId],
      statsigStableId: this.statsigStableId,
    };
  };

  private withFacebookPixelCookies = (properties?: EventProperties) => {
    const fbcCookie = getCookie('_fbc');
    const fbpCookie = getCookie('_fbp');
    return {
      ...properties,
      ...(fbcCookie && { fbc: fbcCookie }),
      ...(fbpCookie && { fbp: fbpCookie }),
    };
  };

  private withRedditProperties = (properties?: EventProperties) => {
    const redditUuidCookie = getCookie('_rdt_uuid');
    const redditCid = getRedditCid(document.location.href);
    return {
      ...properties,
      ...(redditUuidCookie && { rdt_uuid: redditUuidCookie }),
      ...(redditCid && { rdt_cid: redditCid }),
    };
  };

  private withPageInfo = (properties?: EventProperties) => {
    return {
      path: document.location.pathname,
      referrer: document.referrer,
      title: document.title,
      url: document.location.href,
      search: document.location.search,
      ...properties,
    };
  };

  private debugAnalyticsEvent(...args: unknown[]) {
    // eslint-disable-next-line no-process-env -- dev feature only
    if (process.env.DEBUG_ANALYTICS === 'true') {
      console.log(...args); // eslint-disable-line no-console
    }
  }
}

declare global {
  interface Window {
    dataLayer?: DataLayer[];
    ouraAnalytics: OuraAnalytics;
  }
}

// initialize global analytics class in the browser
if (isBrowser()) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  window.ouraAnalytics = window?.ouraAnalytics ?? new OuraAnalytics();
}
